import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { useI18n } from '../../i18n/useI18n';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: {
    impact: number;
    reach: number;
    duration: number;
  };
  sourceName: string;
  publishedAt: string;
}

interface NewsCardProps {
  article: NewsArticle;
  featured?: boolean;
}

const NewsCard: React.FC<NewsCardProps> = ({ article, featured = false }) => {
  const { t, locale } = useI18n();

  const getBiasLevel = (score: number) => {
    if (score <= 3) return { level: 'low', color: 'bias-low', text: t('card.lowBias') };
    if (score <= 6) return { level: 'medium', color: 'bias-medium', text: t('card.medBias') };
    return { level: 'high', color: 'bias-high', text: t('card.highBias') };
  };

  const getImpactLevel = (score: number) => {
    if (score >= 500) return { level: 'high', color: 'impact-high', text: t('card.highImpact') };
    if (score >= 200) return { level: 'medium', color: 'impact-medium', text: t('card.medImpact') };
    return { level: 'low', color: 'impact-low', text: t('card.lowImpact') };
  };

  const bias = getBiasLevel(article.biasScore);
  const impact = getImpactLevel(article.noticiabilityTotal);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get translated category name
  const categoryKey = article.category.split(' & ')[0].split(' ')[0]; // "Geopolitics", "Global" -> "Geopolitics", "Economy"
  const catMap: Record<string, string> = {
    'Geopolitics': 'Geopolitics',
    'Global': 'Economy',
    'Environment': 'Climate',
    'Technology': 'Technology',
  };
  const catKey = catMap[categoryKey] || categoryKey;
  const displayCategory = t(`cat.${catKey}`) !== `cat.${catKey}` ? t(`cat.${catKey}`) : article.category;

  if (featured) {
    return (
      <article className="news-card col-span-full md:col-span-2">
        {article.imageUrl && (
          <div className="relative">
            <img src={article.imageUrl} alt={article.title} className="w-full h-64 object-cover" />
            <div className="absolute top-4 left-4">
              <span className="category-badge">{displayCategory}</span>
            </div>
            <div className="absolute top-4 right-4 flex space-x-2">
              <span className={`impact-score ${impact.color}`}>
                <TrendingUp className="h-3 w-3 mr-1" />
                {impact.text}
              </span>
            </div>
          </div>
        )}
        
        <div className="p-8">
          <Link to={`/article/${article.id}`}>
            <h2 className="font-playfair text-3xl font-bold text-primary-900 leading-tight mb-4 hover:text-accent-600 transition-colors">
              {article.title}
            </h2>
          </Link>
          
          <p className="text-primary-700 text-lg leading-relaxed mb-6">
            {article.summary}
          </p>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <span className="font-medium text-primary-600">{article.sourceName}</span>
              <span className="flex items-center text-primary-500">
                <Clock className="h-4 w-4 mr-1" />
                {formatDate(article.publishedAt)}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className={`bias-indicator ${bias.color}`}>
                <AlertTriangle className="h-3 w-3 mr-1" />
                {bias.text}
              </span>
              <span className="text-primary-500 text-xs">
                {t('card.score')}: {article.noticiabilityTotal.toFixed(0)}
              </span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="news-card">
      {article.imageUrl && (
        <div className="relative">
          <img src={article.imageUrl} alt={article.title} className="news-card-image" />
          <div className="absolute top-3 left-3">
            <span className="category-badge">{displayCategory}</span>
          </div>
        </div>
      )}
      
      <div className="news-card-content">
        <Link to={`/article/${article.id}`}>
          <h3 className="news-card-title">{article.title}</h3>
        </Link>
        
        <p className="news-card-summary">
          {article.summary.substring(0, 150)}...
        </p>
        
        <div className="news-card-meta">
          <div className="flex items-center space-x-2">
            <span className="text-primary-600 font-medium">{article.sourceName}</span>
            <span className="text-primary-400">•</span>
            <span className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatDate(article.publishedAt)}
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <span className={`impact-score ${impact.color}`}>
              {impact.level === 'high' && <TrendingUp className="h-3 w-3" />}
              {article.noticiabilityTotal.toFixed(0)}
            </span>
          </div>
        </div>
        
        {article.noticiabilityTotal > 400 && (
          <div className="mt-3 pt-3 border-t border-primary-100">
            <div className="flex justify-between text-xs text-primary-600">
              <span>{t('article.impact')}: {article.scoreJson.impact}/10</span>
              <span>{t('article.globalReach')}: {article.scoreJson.reach}/10</span>
              <span>{t('article.duration')}: {article.scoreJson.duration}/10</span>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default NewsCard;